import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Shuffle, Sun, Moon, Database, LockKey } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";

// Conjuntos de caracteres
const LETTERS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const NUMBERS = "0123456789";
const SPECIALS = "!@#$%&*?+-_=";

// Char aleatório de uma string
const pickChar = (pool) => pool[Math.floor(Math.random() * pool.length)];

// Embaralha (Fisher-Yates)
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Gera senha garantindo ao menos 1 caractere de cada categoria marcada.
 * Retorna string vazia se nenhuma categoria marcada.
 */
function buildPassword({ length, letters, numbers, specials }) {
  const pools = [];
  const forced = [];
  if (letters) {
    pools.push(LETTERS);
    forced.push(pickChar(LETTERS));
  }
  if (numbers) {
    pools.push(NUMBERS);
    forced.push(pickChar(NUMBERS));
  }
  if (specials) {
    pools.push(SPECIALS);
    forced.push(pickChar(SPECIALS));
  }
  if (pools.length === 0) return "";

  const fullPool = pools.join("");
  const remainingCount = Math.max(0, length - forced.length);
  const rest = Array.from({ length: remainingCount }, () => pickChar(fullPool));

  return shuffle([...forced, ...rest]).join("").slice(0, length);
}

function SenhaPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [length, setLength] = useState(12);
  const [letters, setLetters] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [specials, setSpecials] = useState(true);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const noneSelected = !letters && !numbers && !specials;

  const gerarNova = useCallback(() => {
    setPassword(buildPassword({ length, letters, numbers, specials }));
  }, [length, letters, numbers, specials]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  // Regenera automaticamente ao mudar opções (feedback imediato)
  useEffect(() => {
    gerarNova();
  }, [gerarNova]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  const copyPassword = async () => {
    if (!password) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(password);
      } else {
        const ta = document.createElement("textarea");
        ta.value = password;
        ta.style.position = "fixed";
        ta.style.left = "-999999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Silent
    }
  };

  const clampLength = (v) => {
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return 8;
    return Math.max(4, Math.min(64, n));
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] flex items-center justify-center shadow-lg shadow-primary/20">
              <Database size={18} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Gerador de Senhas</h1>
              <p className="text-[11px] text-muted-foreground -mt-0.5 hidden sm:block">
                Ferramenta rápida do ClickDados
              </p>
            </div>
          </div>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            className="w-9 h-9 rounded-xl bg-secondary hover:bg-accent flex items-center justify-center transition-all duration-200 hover:scale-105"
            aria-label="Toggle theme"
          >
            {darkMode ? (
              <Sun size={18} weight="bold" className="text-amber-500" />
            ) : (
              <Moon size={18} weight="bold" className="text-slate-600" />
            )}
          </button>
        </div>
      </header>

      {/* Voltar */}
      <div className="max-w-5xl w-full mx-auto px-6 lg:px-8 pt-6">
        <Link
          to="/"
          data-testid="back-to-home-link"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={16} weight="bold" />
          Voltar para o ClickDados
        </Link>
      </div>

      {/* Main */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 lg:px-8 py-10 sm:py-14">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3">Gerar Senha Segura</h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Senha aleatória local, sem envio para servidor
          </p>
        </div>

        {/* Card com a senha (clique para copiar + botão Gerar ao lado) */}
        <motion.div
          key={`sen-${password}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-card rounded-2xl border border-border/50 shadow-sm p-6 sm:p-8 mb-6"
          data-testid="card-senha"
        >
          <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-widest">
            Senha
          </label>
          <div className="mt-3 flex items-center gap-3 sm:gap-4">
            {/* Botão Gerar à esquerda */}
            <button
              data-testid="generate-new-btn"
              onClick={gerarNova}
              disabled={noneSelected}
              aria-label="Gerar nova senha"
              className="btn-primary shrink-0 h-12 w-12 sm:h-14 sm:w-14 rounded-xl text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
            >
              <Shuffle size={20} weight="bold" />
            </button>

            {/* Senha clicável (copia ao clicar) */}
            <div
              data-testid="value-senha"
              onClick={copyPassword}
              role={password ? "button" : undefined}
              tabIndex={password ? 0 : -1}
              onKeyDown={(e) => {
                if (password && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  copyPassword();
                }
              }}
              className={`flex-1 min-w-0 font-mono font-bold tracking-wider text-2xl sm:text-3xl md:text-4xl leading-tight break-all select-all ${
                password
                  ? "cursor-pointer hover:text-primary transition-colors"
                  : ""
              }`}
              title={password ? "Clique para copiar" : ""}
            >
              {password || (
                <span className="text-muted-foreground/50 font-normal text-base">
                  Selecione ao menos uma opção
                </span>
              )}
            </div>

            {/* Ícone feedback de copiado */}
            {password && (
              <div
                data-testid="copy-feedback"
                aria-live="polite"
                className="shrink-0 w-10 flex items-center justify-center text-muted-foreground"
              >
                {copied ? (
                  <Check size={22} weight="bold" className="text-emerald-500" />
                ) : (
                  <Copy size={20} weight="bold" />
                )}
              </div>
            )}
          </div>
          {password && (
            <p className="mt-3 text-xs text-muted-foreground">
              {copied ? (
                <span className="text-emerald-600 dark:text-emerald-400 font-medium">Copiado!</span>
              ) : (
                "Clique na senha para copiar"
              )}
            </p>
          )}
        </motion.div>

        {/* Opções */}
        <div className="bg-card rounded-2xl border border-border/50 shadow-sm p-6 sm:p-8 mb-6">
          {/* Tamanho */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label htmlFor="length-input" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Quantidade de caracteres
              </label>
              <span data-testid="length-value" className="text-sm font-mono font-semibold">
                {length}
              </span>
            </div>
            <input
              id="length-input"
              data-testid="length-slider"
              type="range"
              min="4"
              max="64"
              value={length}
              onChange={(e) => setLength(clampLength(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-primary bg-secondary"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground/60 mt-1">
              <span>4</span>
              <span>64</span>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-secondary/30 cursor-pointer hover:bg-secondary/60 transition-colors">
              <Checkbox
                data-testid="opt-letters"
                checked={letters}
                onCheckedChange={setLetters}
                className="rounded-md border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary checkbox-modern"
              />
              <span className="text-sm font-medium">Letras (a-z, A-Z)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-secondary/30 cursor-pointer hover:bg-secondary/60 transition-colors">
              <Checkbox
                data-testid="opt-numbers"
                checked={numbers}
                onCheckedChange={setNumbers}
                className="rounded-md border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary checkbox-modern"
              />
              <span className="text-sm font-medium">Números (0-9)</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-secondary/30 cursor-pointer hover:bg-secondary/60 transition-colors">
              <Checkbox
                data-testid="opt-specials"
                checked={specials}
                onCheckedChange={setSpecials}
                className="rounded-md border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary checkbox-modern"
              />
              <span className="text-sm font-medium">Especiais (!@#$…)</span>
            </label>
          </div>

          {noneSelected && (
            <p data-testid="no-option-warning" className="text-xs text-amber-600 dark:text-amber-400 mt-3 text-center">
              Selecione ao menos uma categoria para gerar uma senha.
            </p>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-14 pt-8 border-t border-border/50">
          <div className="text-center space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed max-w-md mx-auto">
              Senha gerada 100% no navegador. Nenhum dado é salvo ou enviado para APIs externas.
            </p>
            <p className="text-xs text-muted-foreground/50">Desenvolvido por Erickson</p>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default SenhaPage;
